import React, {RefObject, useEffect, useRef, useState} from "react";
import styled from "styled-components";
import {useNavigate} from "react-router-dom";
import {AssemblySetup} from "../Assembly/AssemblyTypes";
import {useOutsideAlerter} from "../Utility/useOutsideAlerter";

const T = {
    Container: styled.div<{ $top: number; $left: number; $positionAbove: boolean }>`
      position: fixed;
      top: ${props => props.$top}px;
      left: ${props => props.$left}px;
      background: linear-gradient(180deg, #ffffff 0%, #f7fbff 100%);
      border: 1px solid #cbd5e1;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(10, 31, 68, 0.15);
      padding: 12px;
      z-index: 1000;
      min-width: 180px;
      max-width: 280px;
      animation: fadeIn 0.15s ease-in;

      @keyframes fadeIn {
        from {
          opacity: 0;
          transform: translateY(${props => props.$positionAbove ? '4px' : '-4px'});
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
    `,
    Header: styled.div`
      font-size: 11px;
      font-weight: 600;
      color: #64748b;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      margin-bottom: 8px;
      padding-bottom: 6px;
      border-bottom: 1px solid #e2ecf7;
    `,
    SetupList: styled.div`
      display: flex;
      flex-direction: column;
      gap: 4px;
    `,
    SetupButton: styled.button`
      background: #f1f5f9;
      border: 1px solid #cbd5e1;
      border-radius: 6px;
      padding: 6px 10px;
      font-size: 12px;
      color: #0b3d91;
      cursor: pointer;
      text-align: left;
      transition: all 0.15s ease;
      font-weight: 500;

      &:hover {
        background: #dbeafe;
        border-color: #3b82f6;
        color: #1e40af;
        transform: translateX(2px);
      }

      &:active {
        background: #bfdbfe;
      }
    `,
    EmptyMessage: styled.div`
      font-size: 11px;
      color: #64748b;
      font-style: italic;
      padding: 4px 0;
    `,
};

type ResourceTooltipProps = {
    resourceId: string;
    resourceName: string;
    assemblySetups: AssemblySetup[];
    anchorRef: RefObject<HTMLElement>;
    onClose: () => void;
};

export function ResourceTooltip({resourceId, resourceName, assemblySetups, anchorRef, onClose}: ResourceTooltipProps) {
    const navigate = useNavigate();
    const tooltipRef = useRef<HTMLDivElement>(null);
    const [position, setPosition] = useState({top: 0, left: 0, positionAbove: false});

    useOutsideAlerter(tooltipRef, onClose);

    useEffect(() => {
        if (!anchorRef.current) return;

        const anchorRect = anchorRef.current.getBoundingClientRect();
        const tooltipHeight = 200; // Estimated max height
        const tooltipWidth = 280; // Max width from styled component
        const padding = 8;

        // Calculate if there's space below
        const spaceBelow = window.innerHeight - anchorRect.bottom;
        const spaceAbove = anchorRect.top;
        const positionAbove = spaceBelow < tooltipHeight && spaceAbove > spaceBelow;

        // Calculate vertical position
        let top = positionAbove
            ? anchorRect.top - tooltipHeight - padding
            : anchorRect.bottom + padding;

        // Ensure tooltip doesn't go off top of screen
        if (top < 0) {
            top = padding;
        }

        // Calculate horizontal position (centered on anchor)
        let left = anchorRect.left + (anchorRect.width / 2) - (tooltipWidth / 2);

        // Ensure tooltip doesn't go off left edge
        if (left < padding) {
            left = padding;
        }

        // Ensure tooltip doesn't go off right edge
        if (left + tooltipWidth > window.innerWidth - padding) {
            left = window.innerWidth - tooltipWidth - padding;
        }

        setPosition({top, left, positionAbove});
    }, [anchorRef]);

    const handleSetupClick = (setupId: string) => {
        navigate(`/assembly?setup=${setupId}`);
        onClose();
    };

    return (
        <T.Container ref={tooltipRef} $top={position.top} $left={position.left} $positionAbove={position.positionAbove}>
            <T.Header>
                {resourceName} — Used in Assembly
            </T.Header>
            {assemblySetups.length > 0 ? (
                <T.SetupList>
                    {assemblySetups.map(setup => (
                        <T.SetupButton
                            key={setup.id}
                            onClick={() => handleSetupClick(setup.id)}
                            title={`Click to view ${setup.name}`}
                        >
                            {setup.name}
                        </T.SetupButton>
                    ))}
                </T.SetupList>
            ) : (
                <T.EmptyMessage>Not used in any assembly setup</T.EmptyMessage>
            )}
        </T.Container>
    );
}
